import { Download, FileText, File, Archive, Sheet } from "lucide-react";

export type Attachment = {
  id: number;
  filename: string;
  mime_type: string;
  size: number;
  url: string;
};

function fmtBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function FileIcon({ mime }: { mime: string }) {
  if (mime.includes("pdf")) return <FileText className="w-4 h-4 text-red-400" />;
  if (mime.includes("zip") || mime.includes("archive")) return <Archive className="w-4 h-4 text-yellow-500" />;
  if (mime.includes("sheet") || mime.includes("excel")) return <Sheet className="w-4 h-4 text-emerald-500" />;
  return <File className="w-4 h-4 text-gray-400" />;
}

export default function AttachmentChips({ attachments }: { attachments?: Attachment[] }) {
  if (!attachments?.length) return null;

  const images = attachments.filter((a) => a.mime_type.startsWith("image/"));
  const files  = attachments.filter((a) => !a.mime_type.startsWith("image/"));

  return (
    <div className="mt-3 space-y-2">
      {/* Image thumbnails */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((img) => (
            <a
              key={img.id}
              href={img.url}
              target="_blank"
              rel="noopener noreferrer"
              className="relative group rounded-xl overflow-hidden border border-gray-200 hover:border-indigo-300 transition-colors block"
              title={img.filename}
            >
              <img
                src={img.url}
                alt={img.filename}
                className="w-24 h-24 object-cover block"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                <Download className="w-5 h-5 text-white" />
                <span className="text-[9px] text-white font-bold">{fmtBytes(img.size)}</span>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* File chips */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((f) => (
            <a
              key={f.id}
              href={f.url}
              target="_blank"
              rel="noopener noreferrer"
              download={f.filename}
              className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl hover:bg-white hover:border-gray-300 hover:shadow-sm transition-all max-w-[220px] group"
              title={f.filename}
            >
              <FileIcon mime={f.mime_type} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-gray-700 truncate leading-tight">{f.filename}</p>
                <p className="text-[10px] text-gray-400">{fmtBytes(f.size)}</p>
              </div>
              <Download className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-600 transition-colors shrink-0" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
